import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RefreshCcw, ShoppingCart, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import UomSelector, { defaultUomFor } from "@/components/UomSelector";

const round2 = (n) => Math.round((n || 0) * 100) / 100;

const STATUS_BG = {
  booked: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  dispatched: "bg-orange-100 text-orange-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
  placed: "bg-yellow-100 text-yellow-800",
  accepted: "bg-blue-100 text-blue-800",
  partial_fulfilled: "bg-orange-100 text-orange-800",
  fulfilled: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-700",
};

// Order can be edited only before it's fully fulfilled. Before any
// fulfillment (`placed`/`booked`/`accepted`/`approved`/`dispatched`) it's a
// full edit — add a SKU, remove a line, change qty. Once fulfillment has
// started (`partial_fulfilled`) lines are frozen to qty-reduce-only, no
// add/remove (backend enforces the floor at the already-fulfilled amount).
// `fulfilled` and other terminal states can't be edited at all.
const FULL_EDIT_STATUSES = ["placed", "booked", "accepted", "approved", "dispatched"];
const REDUCE_ONLY_STATUSES = ["partial_fulfilled"];
const EDITABLE_STATUSES = [...FULL_EDIT_STATUSES, ...REDUCE_ONLY_STATUSES];

export function OrdersList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders", { params: { page_size: 50, status: status || undefined } });
      setItems(data.items || []);
      const states = ["placed", "accepted", "partial_fulfilled", "fulfilled", "rejected"];
      const [allR, ...perR] = await Promise.all([
        api.get("/orders", { params: { page_size: 1 } }),
        ...states.map(s => api.get("/orders", { params: { page_size: 1, status: s } })),
      ]);
      const c = { all: allR.data.total || 0 };
      states.forEach((s, i) => { c[s] = perR[i].data.total || 0; });
      setCounts(c);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const STATES = [
    ["", "All"], ["placed", "Placed"], ["accepted", "Accepted"],
    ["fulfilled", "Fulfilled"], ["partial_fulfilled", "Partial Fulfilled"],
    ["rejected", "Rejected"],
  ];

  return (
    <div className="space-y-4 p-4" data-testid="orders-list-screen">
      <PageHeader
        breadcrumbs={[{ label: "Orders" }]}
        title="Orders"
        subtitle="Secondary orders raised from retail outlets."
        favorite={{ key: "orders.list", type: "screen", label: "Orders", route: "/orders" }}
        testid="orders-page-header"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Orders</h1>
        </div>
        <Button variant="outline" onClick={load} data-testid="orders-refresh">
          <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Phase 21C — State filter chips with count badges */}
      <div className="flex flex-wrap gap-1.5" data-testid="orders-state-filters">
        {STATES.map(([code, label]) => {
          const active = status === code;
          const count = code === "" ? counts.all : counts[code];
          return (
            <button key={code || "all"} type="button"
                    onClick={() => setStatus(code)}
                    data-testid={`orders-chip-${code || "all"}`}
                    data-active={active ? "true" : "false"}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
                      (active
                        ? "bg-[#6D2C91] border-[#6D2C91] text-white"
                        : "bg-white border-neutral-200 text-neutral-700 hover:border-[#6D2C91]")
                    }>
              <span>{label}</span>
              <span className={
                "inline-flex min-w-[1.5rem] justify-center rounded-full px-1 text-[10px] leading-4 " +
                (active ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600")
              }>{count ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2" data-testid="orders-list">
        {items.length === 0 && !loading && (
          <div className="py-10 text-center text-neutral-400"><ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />No orders</div>
        )}
        {items.map(o => (
          <Card key={o.id} className="border-neutral-200 rounded-2xl cursor-pointer hover:border-[#E5197F]"
                onClick={() => nav(`/orders/${o.id}`)}
                data-testid={`order-row-${o.order_no}`}>
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-mono text-neutral-400">{o.order_no}</div>
                <div className="text-sm font-semibold text-neutral-900 truncate">{o.retailer_name}</div>
                <div className="text-[11px] text-neutral-500">
                  {o.user_name} · {new Date(o.booking_ts).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-neutral-900">₹{o.net_amount}</div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${STATUS_BG[o.status] || ""}`}>
                  {o.status}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function OrderDetail() {
  const { order_id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${order_id}`);
    setOrder(data);
  }, [order_id]);
  useEffect(() => { load(); }, [load]);

  if (!order) return <div className="p-4 text-sm text-neutral-500">Loading…</div>;

  const canChange = !["delivered", "cancelled", "fulfilled", "rejected"].includes(order.status);
  const nextStatuses = {
    booked: ["approved", "cancelled"],
    approved: ["dispatched", "cancelled"],
    dispatched: ["delivered", "cancelled"],
    placed: ["accepted", "rejected"],
    accepted: ["rejected"],
    partial_fulfilled: ["rejected"],
  }[order.status] || [];
  const canEdit = EDITABLE_STATUSES.includes(order.status);

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="order-detail-screen">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500">← Back</button>

      <div>
        <div className="text-[11px] font-mono text-neutral-400">{order.order_no}</div>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-extrabold text-neutral-900">{order.retailer?.name}</h1>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => setEditDialog(true)}
                      data-testid="edit-order-btn">
                Edit
              </Button>
            )}
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${STATUS_BG[order.status]}`}>
              {order.status}
            </span>
          </div>
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">
          Booked by {order.user?.name} · {new Date(order.booking_ts).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="text-sm font-semibold mb-2">Lines ({order.lines.length})</div>
          <div className="space-y-1 text-sm">
            {order.lines.map(l => (
              <div key={l.id} className="flex justify-between border-b border-neutral-100 py-1">
                <div>
                  <div className="text-neutral-900">{l.sku_name_snapshot}</div>
                  <div className="text-[11px] text-neutral-500">
                    {l.sku_code_snapshot} · {l.qty_kg ?? 0} kg
                    {l.entered_uom && l.entered_uom !== "PCS" ? ` (entered ${l.entered_qty} ${l.entered_uom})` : ""}
                    · @ ₹{l.unit_price}
                  </div>
                  {l.fulfilled_qty_pcs > 0 && (
                    <div className="text-[11px] text-emerald-700" data-testid={`line-fulfilled-${l.id}`}>
                      ✓ Fulfilled {l.fulfilled_qty_kg ?? 0} kg
                      {l.fulfilled_uom && l.fulfilled_uom !== "PCS"
                        ? ` (last: ${l.fulfilled_qty_entered} ${l.fulfilled_uom})`
                        : ""}
                    </div>
                  )}
                  {l.scheme_code_snapshot && (
                    <div className="text-[10px] text-green-700">
                      Scheme: {l.scheme_code_snapshot}
                      {l.scheme_benefit_type === "free_item_note" && l.scheme_benefit_value && ` · ${l.scheme_benefit_value}`}
                      {l.scheme_benefit_type === "free_qty" && l.free_qty > 0 && ` · +${l.free_qty} pcs free`}
                      {(l.discount_amount || 0) > 0 && ` · -₹${l.discount_amount}`}
                    </div>
                  )}
                </div>
                <div className="text-neutral-900 font-mono">₹{l.line_total}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 border-t border-neutral-200 pt-2 space-y-1 text-sm">
            <div className="flex justify-between text-neutral-500"><span>Total KG</span><span>{order.lines.reduce((s, l) => s + (l.qty_kg || 0), 0).toFixed(2)} kg</span></div>
            <div className="flex justify-between text-neutral-500"><span>Gross</span><span>₹{order.gross_amount}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Scheme Discount</span><span>-₹{order.scheme_discount_amount}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Tax</span><span>₹{order.tax_amount}</span></div>
            <div className="flex justify-between font-bold text-lg"><span>Net</span><span className="text-[#E5197F]">₹{order.net_amount}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 26D.2 — Fulfillment card (KG/PCS/BAG entry, independent of ordering unit).
          Shows for accepted/partial_fulfilled AND the legacy approved/dispatched
          statuses (backend normalises them all to the acceptance bucket). */}
      {["accepted", "partial_fulfilled", "approved", "dispatched"].includes(order.status) && (
        <FulfillmentCard order={order} onDone={load} />
      )}

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="text-sm font-semibold mb-2">Status timeline</div>
          <div className="space-y-2">
            {(order.status_log || []).map(l => (
              <div key={l.id} className="flex justify-between text-sm">
                <div>
                  {l.from_status ? `${l.from_status} →` : "→"} <span className="font-semibold">{l.to_status}</span>
                  {l.notes && <div className="text-xs text-neutral-500 italic">"{l.notes}"</div>}
                </div>
                <div className="text-[11px] font-mono text-neutral-400">
                  {new Date(l.changed_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {canChange && nextStatuses.length > 0 && (
        <Button onClick={() => setStatusDialog({ to: nextStatuses[0] })}
                className="w-full h-12 tulsi-gradient text-white rounded-xl"
                data-testid="change-status-btn">
          Change status
        </Button>
      )}

      {statusDialog && (
        <StatusChangeDialog order={order} next={nextStatuses} initial={statusDialog.to}
                            onClose={() => setStatusDialog(null)}
                            onDone={() => { setStatusDialog(null); load(); }} />
      )}

      {editDialog && (
        <EditOrderDialog order={order}
                          onClose={() => setEditDialog(false)}
                          onDone={() => { setEditDialog(false); load(); }} />
      )}
    </div>
  );
}

// Edit an order's lines. Before any fulfillment (placed/booked/accepted/
// approved/dispatched) this is a full edit: change qty, remove a line, add a
// new SKU. Once `partial_fulfilled`, existing lines can only be reduced (and
// never below what's already fulfilled) — no add/remove — the backend
// enforces all of this too, this just keeps the UI bounded up front.
function EditOrderDialog({ order, onClose, onDone }) {
  const reduceOnly = order.status === "partial_fulfilled";
  const [qtys, setQtys] = useState(() => {
    const m = {};
    order.lines.forEach(l => { m[l.id] = String(l.qty_pcs ?? l.qty ?? 0); });
    return m;
  });
  const [removedIds, setRemovedIds] = useState(() => new Set());
  const [newLines, setNewLines] = useState([]); // { key, sku_id, qty_pcs }
  const [skus, setSkus] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (reduceOnly) return;
    let cancelled = false;
    api.get("/skus", { params: { page_size: 500, is_orderable: true } }).then(({ data }) => {
      if (!cancelled) setSkus(data.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [reduceOnly]);

  const toggleRemove = (lineId) => setRemovedIds(prev => {
    const next = new Set(prev);
    next.has(lineId) ? next.delete(lineId) : next.add(lineId);
    return next;
  });

  const addLine = () => setNewLines(prev => [...prev, { key: `new-${Date.now()}-${prev.length}`, sku_id: "", qty_pcs: "" }]);
  const updateNewLine = (key, patch) => setNewLines(prev => prev.map(nl => nl.key === key ? { ...nl, ...patch } : nl));
  const removeNewLine = (key) => setNewLines(prev => prev.filter(nl => nl.key !== key));

  const submit = async () => {
    if (!reduceOnly && removedIds.size === order.lines.length && newLines.length === 0) {
      toast.error("Cannot remove every line from an order");
      return;
    }
    const lines = [];
    for (const l of order.lines) {
      if (removedIds.has(l.id)) {
        lines.push({ line_id: l.id, remove: true });
        continue;
      }
      const val = qtys[l.id];
      const qty_pcs = val === "" ? null : Number(val);
      const prev = l.qty_pcs ?? l.qty ?? 0;
      const fulfilled = l.fulfilled_qty_pcs || 0;
      if (qty_pcs === null || qty_pcs === prev) continue;
      if (reduceOnly && qty_pcs > prev) {
        toast.error("Qty can only be reduced on a partially fulfilled order");
        return;
      }
      if (qty_pcs < fulfilled) {
        toast.error(`Qty can't go below the ${fulfilled} already fulfilled`);
        return;
      }
      lines.push({ line_id: l.id, qty_pcs });
    }
    for (const nl of newLines) {
      if (!nl.sku_id || !nl.qty_pcs) continue;
      lines.push({ sku_id: nl.sku_id, qty_pcs: Number(nl.qty_pcs) });
    }
    if (lines.length === 0) {
      toast.error("No changes to save");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}`, { lines });
      toast.success("Order updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-order-dialog">
        <DialogHeader><DialogTitle>Edit order</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {reduceOnly && (
            <div className="text-[11px] text-neutral-500">
              This order is partially fulfilled — qty can only be reduced, and not below what's already fulfilled. Lines can't be added or removed.
            </div>
          )}
          {order.lines.map(l => {
            const fulfilled = l.fulfilled_qty_pcs || 0;
            const removed = removedIds.has(l.id);
            return (
              <div key={l.id}
                   className={`flex items-center justify-between gap-3 border rounded-lg p-2 ${removed ? "border-red-200 bg-red-50 opacity-60" : "border-neutral-200"}`}>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-neutral-900 truncate">{l.sku_name_snapshot}</div>
                  {fulfilled > 0 && (
                    <div className="text-[10px] text-emerald-700">Fulfilled: {(l.fulfilled_qty_kg ?? 0)} kg</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={reduceOnly ? fulfilled : 0} step="1"
                    value={qtys[l.id]}
                    disabled={removed}
                    onChange={e => setQtys(q => ({ ...q, [l.id]: e.target.value }))}
                    className="w-24 h-9 px-2 rounded border border-neutral-200 text-sm text-right disabled:opacity-50"
                    data-testid={`edit-qty-${l.id}`}
                  />
                  {!reduceOnly && (
                    <button type="button" onClick={() => toggleRemove(l.id)}
                            disabled={fulfilled > 0}
                            className="text-xs text-red-600 hover:underline disabled:opacity-30 disabled:no-underline"
                            data-testid={`edit-remove-${l.id}`}>
                      {removed ? "Undo" : "Remove"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {!reduceOnly && newLines.map(nl => (
            <div key={nl.key} className="flex items-center justify-between gap-2 border border-dashed border-neutral-300 rounded-lg p-2">
              <Select value={nl.sku_id} onValueChange={v => updateNewLine(nl.key, { sku_id: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid={`edit-new-sku-${nl.key}`}>
                  <SelectValue placeholder="Choose SKU" />
                </SelectTrigger>
                <SelectContent>
                  {skus.map(s => <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <input
                type="number" min="1" step="1" placeholder="Qty"
                value={nl.qty_pcs}
                onChange={e => updateNewLine(nl.key, { qty_pcs: e.target.value })}
                className="w-24 h-9 px-2 rounded border border-neutral-200 text-sm text-right"
                data-testid={`edit-new-qty-${nl.key}`}
              />
              <button type="button" onClick={() => removeNewLine(nl.key)}
                      className="text-xs text-neutral-400 hover:text-red-600">✕</button>
            </div>
          ))}

          {!reduceOnly && (
            <Button variant="outline" size="sm" onClick={addLine} data-testid="edit-add-sku-btn">
              + Add SKU
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="tulsi-gradient text-white"
                  data-testid="edit-order-submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusChangeDialog({ order, next, initial, onClose, onDone }) {
  const [to, setTo] = useState(initial);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isCancel = to === "cancelled";
  const submit = async () => {
    if (isCancel && !notes.trim()) return toast.error("Cancellation reason required");
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/status`, { to_status: to, notes: notes.trim() || null });
      toast.success(`Status → ${to}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="status-change-dialog">
        <DialogHeader><DialogTitle>Change status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={to} onValueChange={setTo}>
            <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {next.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder={isCancel ? "Cancellation reason (required)" : "Notes (optional)"}
                    data-testid="status-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className={isCancel ? "bg-red-600 text-white" : "tulsi-gradient text-white"}
                  data-testid="status-submit">
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 26D.2 — Fulfillment card. Renders inside OrderDetail whenever the
// order is in `accepted` or `partial_fulfilled`. For each un-fulfilled line
// the operator chooses the fulfillment UOM (PCS/KG/BAG, independent of what
// the order was booked in) and enters the delivered quantity. Server
// canonicalises via convert_to_canonical + updates fulfilled_qty_pcs / _kg.
function FulfillmentCard({ order, onDone }) {
  const [skus, setSkus] = useState({});
  const [entries, setEntries] = useState({}); // line_id → { uom, qty }
  const [busy, setBusy] = useState(false);

  // Load SKU allowed_uoms via the list endpoint (individual GET strips these
  // fields, but list includes them). One call, indexed by id.
  useEffect(() => {
    let cancelled = false;
    api.get("/skus", { params: { page_size: 500 } }).then(({ data }) => {
      if (cancelled) return;
      const map = {};
      (data.items || []).forEach(s => { map[s.id] = s; });
      setSkus(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [order.id]);

  // Phase 15 — actually seed `entries` state with each line's outstanding
  // qty (not just a display fallback) so "Record fulfillment" has real
  // values to submit even if the admin never touches an input — the qty
  // shown pre-filled must be the qty actually sent.
  useEffect(() => {
    const init = {};
    for (const l of order.lines || []) {
      const outstandingKg = round2((l.qty_kg || 0) - (l.fulfilled_qty_kg || 0));
      if (outstandingKg > 0) init[l.id] = { uom: "KG", qty: String(outstandingKg) };
    }
    setEntries(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const setEntry = (line_id, patch) => setEntries(e => ({
    ...e, [line_id]: { ...(e[line_id] || {}), ...patch },
  }));

  const submit = async () => {
    const payloadLines = Object.entries(entries)
      .filter(([, v]) => v && Number(v.qty) > 0)
      .map(([line_id, v]) => ({
        line_id,
        fulfilled_uom: v.uom,
        fulfilled_qty_entered: Number(v.qty),
      }));
    if (payloadLines.length === 0) {
      toast.error("Enter fulfilled quantity on at least one line");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/fulfill`, { lines: payloadLines });
      toast.success("Fulfillment recorded");
      setEntries({});
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-emerald-200 rounded-2xl" data-testid="fulfillment-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PackageCheck className="h-4 w-4 text-emerald-700" />
          <div className="text-sm font-semibold">Record fulfillment</div>
        </div>
        <div className="text-[11px] text-neutral-500">
          Enter delivered quantity in PCS, KG, or BAG — regardless of how the order was booked.
          Delivering less than the full amount still closes the order — the remainder is written off, not queued for a later round.
        </div>
        <div className="space-y-2">
          {order.lines
            .filter(l => (l.qty_pcs ?? l.qty ?? 0) > (l.fulfilled_qty_pcs || 0))
            .map(l => {
              const sku = skus[l.sku_id];
              // Phase 15 — pre-fill with the outstanding qty (in KG, same
              // convention as the portal's own Fulfill dialog) so the common
              // case — recording the whole outstanding amount — needs no
              // typing; still fully editable for a partial round.
              const outstandingKg = round2((l.qty_kg || 0) - (l.fulfilled_qty_kg || 0));
              const entry = entries[l.id] || {
                uom: "KG",
                qty: outstandingKg > 0 ? String(outstandingKg) : "",
              };
              return (
                <div key={l.id}
                     className="border border-neutral-200 rounded-lg p-2 space-y-1.5"
                     data-testid={`fulfill-line-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-neutral-900">{l.sku_name_snapshot}</div>
                      <div className="text-[10px] text-neutral-500">
                        Outstanding: {outstandingKg} kg
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <UomSelector
                      sku={sku}
                      value={entry.uom || defaultUomFor(sku)}
                      onChange={(u) => setEntry(l.id, { uom: u })}
                      testId={`fulfill-uom-${l.id}`}
                    />
                    <input
                      type="number" min="0" step="any"
                      value={entry.qty ?? ""}
                      onChange={e => setEntry(l.id, { qty: e.target.value })}
                      placeholder="Qty"
                      className="w-24 h-9 px-2 rounded border border-neutral-200 text-sm text-right"
                      data-testid={`fulfill-qty-${l.id}`}
                    />
                  </div>
                </div>
              );
            })}
        </div>
        <Button onClick={submit} disabled={busy}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold"
                data-testid="fulfill-submit">
          {busy ? "Recording…" : "Record fulfillment"}
        </Button>
      </CardContent>
    </Card>
  );
}

